/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useContext } from 'react';
import { apiInternalVisitsPath, checkAvailableTechniciansPath } from 'routes';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { Duration, DateTime } from 'luxon';
import { Controller, useForm } from 'react-hook-form';
import api from '@utils/axios';
import { translate } from '@utils/i18n';
import Select from '@components/Select/Select';
import TimeInput from '@components/TimeInput/TimeInput';
import { Button } from '@ui';
import LoadableButton from '@components/LoadableButton/LoadableButton';
import DateInput from '@components/DateInput/DateInput';
import InputTextArea, { StyleVariant } from '@components/InputTextArea/InputTextArea';
import VisitPriorityDropdown from '@components/Dropdowns/VisitPriorityDropdown';
import { convertUTCToLocalDate, convertLocalDateToUTC } from '@utils/dateUtils';
import { TimeZoneContext } from '@contexts/timezone-context';
import { TenantContext } from '@contexts/tenant-context';
import { Dialog } from '@progress/kendo-react-dialogs';
import { VisitPriority } from './constants';
import TextInput from './components/TextInput';
import FormTypeSelector from './components/FormTypeSelector';

const namespace = 'features.work_orders.visits_section';

export default function NewVisitModal({
  woId,
  isOpen,
  onClose,
  defaultTechnicianId,
  defaultSubcontractorId,
  techOptions,
  vendorOptions,
  statusesOptions,
  onAddComplete,
  defaultAssigneeType,
  defaultVisitStatus,
  priorityOptions,
  isSubcontractorsEnabled,
  defaultTimeZone,
  timeZoneOptions
}) {
  const { timeZonesMapping } = useContext(TimeZoneContext);
  const { tenant } = useContext(TenantContext);
  const [loading, setIsLoading] = useState(false);
  const [isVendor, setIsVendor] = useState(defaultAssigneeType === 'subcontractor');
  const [isNoteVisible, setIsNoteVisible] = useState(false);
  const [checkAvailableNotification, setCheckAvailableNotification] = useState({ message: '', data: null });
  const { register, reset, handleSubmit, control, watch, setValue } = useForm({
    defaultValues: {
      technicianId: defaultTechnicianId || '',
      subcontractorId: defaultSubcontractorId || '',
      status: defaultVisitStatus,
      start: convertUTCToLocalDate(
        DateTime.now().setZone(timeZonesMapping[defaultTimeZone]).toUTC().toString(),
        timeZonesMapping[defaultTimeZone]
      ),
      days: 0,
      hours: 1,
      minutes: 0,
      visitNotes: '',
      notesToVendor: '',
      poNumber: '',
      billingLimit: 0,
      priority: VisitPriority.NORMAL,
      priorityNote: '',
      timeZone: defaultTimeZone
    }
  });

  useEffect(() => {
    if (isOpen) setValue('timeZone', defaultTimeZone);
  }, [isOpen, defaultTimeZone]);

  const priority = watch('priority');
  const isHighPriority = priority === VisitPriority.HIGH;

  useEffect(() => {
    let controller = new AbortController();

    const subscription = watch((value, { name }) => {
      if (!['minutes', 'hours', 'days', 'technicianId', 'start'].includes(name)) return;
      if (!parseInt(value.technicianId, 10)) {
        setCheckAvailableNotification({ message: '', data: null });
        return;
      }

      controller.abort();
      controller = new AbortController();

      api
        .get(checkAvailableTechniciansPath({ format: 'json' }), {
          signal: controller.signal,
          params: {
            technician_id: value.technicianId,
            scheduled_date: convertLocalDateToUTC(value.start, timeZonesMapping[value.timeZone]),
            time_zone: value.timeZone,
            duration: Duration.fromObject({
              days: value.days,
              hours: value.hours,
              minutes: value.minutes
            }).as('minutes')
          }
        })
        .then(() => setCheckAvailableNotification({ message: '', data: null }))
        .catch((error) =>
          setCheckAvailableNotification({
            message: error?.response?.data?.message,
            data: error?.response?.data?.data
          })
        );
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = (newVisitInfo) => {
    if (woId) return onSubmitOnPersisted(newVisitInfo);

    return onSubmitOnNewRecord(newVisitInfo);
  };

  const onSubmitOnNewRecord = (newVisitInfo) => {
    onAddComplete({
      start: convertLocalDateToUTC(newVisitInfo.start, timeZonesMapping[newVisitInfo.timeZone]),
      duration: Number(newVisitInfo.days) * 1440 + Number(newVisitInfo.hours * 60) + Number(newVisitInfo.minutes),
      technician_id: !isVendor ? Number(newVisitInfo.technicianId) || '' : '',
      id: Math.random(),
      notes: isVendor ? newVisitInfo.notesToVendor : undefined,
      visit_status: newVisitInfo.status ? newVisitInfo.status : undefined,
      subcontractor_id: isVendor ? Number(newVisitInfo.subcontractorId) || '' : '',
      subcontractor_po_number: newVisitInfo.poNumber ? newVisitInfo.poNumber : undefined,
      subcontractor_billing_limit: newVisitInfo.billingLimit ? newVisitInfo.billingLimit : undefined,
      assignee_type: isVendor ? 'subcontractor' : 'technician',
      priority: newVisitInfo.priority,
      priority_note: newVisitInfo.priority === VisitPriority.HIGH ? newVisitInfo.priorityNote : null,
      visit_notes: newVisitInfo.visitNotes || undefined,
      time_zone: newVisitInfo.timeZone
    });
    fullReset();
  };

  const onSubmitOnPersisted = async (newVisitInfo) => {
    try {
      setIsLoading(true);
      const visitInfo = {
        scheduled_date: convertLocalDateToUTC(newVisitInfo.start, timeZonesMapping[newVisitInfo.timeZone]),
        duration: Number(newVisitInfo.days) * 1440 + Number(newVisitInfo.hours * 60) + Number(newVisitInfo.minutes),
        technician_id: !isVendor ? newVisitInfo.technicianId ?? null : null,
        work_order_id: woId,
        notes: isVendor ? newVisitInfo.notesToVendor : undefined,
        visit_status: newVisitInfo.status ? newVisitInfo.status : undefined,
        subcontractor_id: isVendor ? newVisitInfo.subcontractorId ?? null : null,
        subcontractor_po_number: newVisitInfo.poNumber ? newVisitInfo.poNumber : undefined,
        subcontractor_billing_limit: newVisitInfo.billingLimit ? newVisitInfo.billingLimit : undefined,
        assignee_type: isVendor ? 'subcontractor' : 'technician',
        priority: newVisitInfo.priority,
        priority_note: newVisitInfo.priority === VisitPriority.HIGH ? newVisitInfo.priorityNote : null,
        visit_notes: newVisitInfo.visitNotes || undefined,
        time_zone_override: newVisitInfo.timeZone
      };

      const { data: res } = await api.post(apiInternalVisitsPath({ format: 'json' }), { visit: visitInfo });

      onAddComplete(res.data);
      fullReset();
    } catch (e) {
      toast.error(translate('add_error', { namespace }), {
        position: 'bottom-right',
        autoClose: 3000,
        closeOnClick: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fullReset = () => {
    reset();
    setIsVendor(defaultAssigneeType === 'subcontractor');
    setCheckAvailableNotification({ message: '', data: null });
  };

  const onClickCancel = () => {
    onClose();
    fullReset();
  };

  const onChangeAsigneeType = ({ isVendor: newIsVendor }) => {
    setIsVendor(newIsVendor);
  };

  const technicianOptions = techOptions.map(([name, id]) => ({ label: name, value: id }));
  const subcontractorOptions = vendorOptions.default.map(([name, id]) => ({ label: name, value: id }));
  const statuses = statusesOptions.map(([name, id]) => ({ label: name, value: id }));
  const priorities = priorityOptions.map(([name, id]) => ({ label: name, value: id }));
  const timeZones = timeZoneOptions.map(([name, id]) => ({ label: name, value: id }));

  if (!isOpen) return null;

  return (
    <Dialog className="qmb-dialog" title={translate('new_visit', { namespace })} onClose={onClose} width={600}>
      <section className="qmb-modal__body visitModal">
        <form className="modalForm" onSubmit={handleSubmit(onSubmit)}>
          {isSubcontractorsEnabled && (
            <div className="formSection">
              <FormTypeSelector isVendor={isVendor} onChange={onChangeAsigneeType} />
            </div>
          )}
          <div className="formSection equalChildrens">
            {!isVendor && (
              <Select
                {...register('technicianId')}
                id="technicianId"
                noOptionLabel={translate('unassigned', { namespace })}
                label={translate('technician', { namespace })}
                options={technicianOptions}
              />
            )}
            {isVendor && (
              <Select
                {...register('subcontractorId')}
                id="subcontractorId"
                noOptionLabel={translate('unassigned', { namespace })}
                label={translate('subcontractor', { namespace })}
                options={subcontractorOptions}
              />
            )}
            <Select {...register('status')} label={translate('status', { namespace })} id="status" options={statuses} />
          </div>
          {isVendor && (
            <div className="formSection">
              <div className="equalChildrens">
                <Controller
                  name="poNumber"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      id="poNumber"
                      value={field.value}
                      name={field.name}
                      onChange={field.onChange}
                      label={`${translate('po', { namespace })} #`}
                    />
                  )}
                />
                <Controller
                  name="billingLimit"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      id="billingLimit"
                      value={field.value}
                      name={field.name}
                      onChange={field.onChange}
                      type="number"
                      min={0}
                      label={translate('billing_limit', { namespace })}
                    />
                  )}
                />
              </div>
            </div>
          )}
          {tenant?.flags?.hasMultiTimezone && (
            <div className="formSection">
              <div className="equalChildrens">
                <Select
                  {...register('timeZone')}
                  id="timeZone"
                  label={translate('time_zone', { namespace })}
                  options={timeZones}
                  required
                />
                <div />
              </div>
            </div>
          )}
          <div className="formSection">
            <div className="equalChildrens">
              <Controller
                name="start"
                control={control}
                render={({ field }) => (
                  <DateInput
                    id="start"
                    value={field.value}
                    name={field.name}
                    onChange={field.onChange}
                    label={translate('scheduled_date', { namespace })}
                  />
                )}
              />
              <Controller
                name="start"
                control={control}
                render={({ field }) => (
                  <TimeInput
                    id="start"
                    value={field.value}
                    name={field.name}
                    onChange={field.onChange}
                    label={translate('start_time', { namespace })}
                  />
                )}
              />
            </div>
            <div className="equalChildrens">
              <span>{translate('duration', { namespace })}:</span>
              <Controller
                name="days"
                control={control}
                render={({ field }) => (
                  <TextInput
                    id="days"
                    value={field.value}
                    name={field.name}
                    onChange={field.onChange}
                    label={translate('days', { namespace: 'features.commons' })}
                    type="number"
                    min={0}
                  />
                )}
              />
              <Controller
                name="hours"
                control={control}
                render={({ field }) => (
                  <TextInput
                    id="hours"
                    type="number"
                    value={field.value}
                    name={field.name}
                    onChange={field.onChange}
                    min={0}
                    label={translate('hours', { namespace: 'features.commons' })}
                  />
                )}
              />
              <Controller
                name="minutes"
                control={control}
                render={({ field }) => (
                  <TextInput
                    id="minutes"
                    type="number"
                    value={field.value}
                    name={field.name}
                    onChange={field.onChange}
                    min={0}
                    label={translate('minutes', { namespace: 'features.commons' })}
                  />
                )}
              />
            </div>
          </div>
          {!isNoteVisible && (
            <Button variant="highlighted" onClick={() => setIsNoteVisible(true)}>
              {translate('add_visit_note', { namespace })}
            </Button>
          )}
          {isNoteVisible && (
            <div className="formSection vertical visit-notes-input-wrapper">
              <Controller
                name="visitNotes"
                control={control}
                render={({ field }) => (
                  <InputTextArea
                    className="visit-note-field"
                    label={translate('visit_notes', { namespace })}
                    value={field.value}
                    name={field.name}
                    buttonControlled={false}
                    variant={StyleVariant.Light}
                    onChange={field.onChange}
                  />
                )}
              />
              <Button
                className="close-note-button close-note-button--kendo"
                aria-label={translate('close', { namespace: 'features.commons' })}
                onClick={() => {
                  setIsNoteVisible(false);
                  setValue('visitNotes', '');
                }}>
                <i className="fa-light fa-xmark" />
              </Button>
            </div>
          )}
          <div className="form__set--vertical--compact">
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <VisitPriorityDropdown
                  visitPriorityList={priorities}
                  selectedValue={field.value}
                  onChange={field.onChange}
                  disabled={false}
                />
              )}
            />

            {isHighPriority && (
              <Controller
                name="priorityNote"
                control={control}
                render={({ field }) => (
                  <InputTextArea
                    label={translate('priority_note', { namespace })}
                    value={field.value}
                    name={field.name}
                    buttonControlled={false}
                    variant={StyleVariant.Light}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </div>
          <div className="modalActionContainer">
            {!isVendor && (
              <div style={{ margin: 'auto' }}>
                {checkAvailableNotification.message}
                <br />
                {checkAvailableNotification.data && (
                  <a href={checkAvailableNotification.data.link} target="_blank" rel="noreferrer">
                    {checkAvailableNotification.data.text_link}
                  </a>
                )}
              </div>
            )}
            <Button onClick={onClickCancel}>{translate('cancel', { namespace: 'features.commons' })}</Button>
            <LoadableButton type="submit" variant="primary" loading={loading}>
              {translate('save', { namespace: 'features.commons' })}
            </LoadableButton>
          </div>
        </form>
      </section>
    </Dialog>
  );
}

NewVisitModal.propTypes = {
  defaultAssigneeType: PropTypes.string.isRequired,
  woId: PropTypes.number,
  vendorOptions: PropTypes.object,
  onAddVisit: PropTypes.func,
  isOpen: PropTypes.bool,
  defaultTechnicianId: PropTypes.number,
  defaultSubcontractorId: PropTypes.number,
  onClose: PropTypes.func,
  techOptions: PropTypes.array,
  priorityOptions: PropTypes.array,
  timeZoneOptions: PropTypes.array,
  statusesOptions: PropTypes.array,
  onAddComplete: PropTypes.func.isRequired,
  isSubcontractorsEnabled: PropTypes.bool.isRequired
};

NewVisitModal.defaultProps = {
  vendorOptions: {},
  woId: null,
  onAddVisit: () => {},
  defaultTechnicianId: null,
  defaultSubcontractorId: null,
  isOpen: false,
  onClose: () => {},
  techOptions: [],
  priorityOptions: [],
  timeZoneOptions: [],
  statusesOptions: []
};
